import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrg } from '../../context/OrgContext';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/apiService';
import CafeStatusManager from '../../features/cafe/components/CafeStatusManager';

const OrganizationSettings: React.FC = () => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [nameError, setNameError] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  
  // Settings state
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsUpdatedBy, setSettingsUpdatedBy] = useState<string | null>(null);
  const [settingsUpdatedDate, setSettingsUpdatedDate] = useState<string | null>(null);
  const [settings, setSettings] = useState('{}');
  const [newSettings, setNewSettings] = useState('{}');
  const [editingSettings, setEditingSettings] = useState(false);
  
  const { currentOrganization, deleteOrganization, isCurrentUserOwner, updateOrganization, selectOrganization, loading } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();

  // Load organization and settings when component mounts
  useEffect(() => {
    const loadOrganization = async () => {
      if (orgSlug && (!currentOrganization || currentOrganization.urlName !== orgSlug)) {
        const org = await selectOrganization(orgSlug);
        if (!org) {
          setError('Organization not found');
        }
      }
    };

    loadOrganization();
  }, [orgSlug, selectOrganization, currentOrganization]);

  const loadSettings = useCallback(async () => {
    if (!orgSlug) return;
    
    try {
      setSettingsLoading(true);
      setSettingsError('');
      
      // Check if the method exists (since it's a new feature)
      if (typeof apiService.getOrganizationSettings === 'function') {
        const response = await apiService.getOrganizationSettings(orgSlug);
        
        if (response.success && response.data) {
          setSettings(response.data.settings);
          setSettingsUpdatedBy(response.data.updatedBy);
          setSettingsUpdatedDate(response.data.updatedDate);
        } else {
          setSettingsError(response.error || 'Failed to load settings');
        }
      } else {
        setSettings('{}'); // Default empty settings
      }
    } catch (error: any) {
      setSettingsError(error.message || 'An error occurred while loading settings');
    } finally {
      setSettingsLoading(false);
    }
  }, [orgSlug]);

  // Load settings when organization is available
  useEffect(() => {
    if (currentOrganization && orgSlug && currentOrganization.urlName === orgSlug) {
      loadSettings();
    }
  }, [currentOrganization, orgSlug, loadSettings]);

  const handleEditSettings = () => {
    setNewSettings(settings);
    setEditingSettings(true);
    setSettingsError('');
  };

  const handleCancelSettingsEdit = () => {
    setEditingSettings(false);
    setNewSettings('{}');
    setSettingsError('');
  };

  const handleSaveSettings = async () => {
    if (!orgSlug) {
      setSettingsError('Organization not found');
      return;
    }

    // Validate JSON
    try {
      JSON.parse(newSettings);
    } catch (jsonError) {
      setSettingsError('Invalid JSON format');
      return;
    }

    try {
      setSettingsLoading(true);
      setSettingsError('');

      // Check if the method exists (since it's a new feature)
      if (typeof apiService.updateOrganizationSettings === 'function') {
        const response = await apiService.updateOrganizationSettings(orgSlug, newSettings);

        if (response.success && response.data) {
          setSettings(response.data.settings);
          setSettingsUpdatedBy(response.data.updatedBy);
          setSettingsUpdatedDate(response.data.updatedDate);
          setEditingSettings(false);
          setNewSettings('{}');
        } else {
          setSettingsError(response.error || 'Failed to save settings');
        }
      } else {
        setSettingsError('Settings update API not implemented yet');
      }
    } catch (error: any) {
      setSettingsError(error.message || 'An error occurred while saving settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
    setDeleteError('');
    setDeleteConfirmText('');
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
    setDeleteError('');
  };

  const handleEditName = () => {
    setNewName(currentOrganization?.name || '');
    setEditingName(true);
    setNameError('');
  };

  const handleCancelEdit = () => {
    setEditingName(false);
    setNewName('');
    setNameError('');
  };

  const handleUpdateName = async () => {
    if (!currentOrganization || !orgSlug) {
      setError('Cafe not found');
      return;
    }

    if (!newName.trim()) {
      setError('Cafe name cannot be empty');
      return;
    }

    if (newName.trim() === currentOrganization.name) {
      setEditingName(false);
      return;
    }

    try {
      setNameError('');
      setUpdateLoading(true);

      const success = await updateOrganization(orgSlug, { name: newName.trim() });

      if (success) {
        setEditingName(false);
        setNewName('');
      } else {
        setNameError('Failed to update cafe name');
      }
    } catch (error: any) {
      console.error('Failed to update cafe name:', error);
      setNameError(error.message || 'An error occurred while updating the cafe name');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!currentOrganization || !orgSlug) {
      setDeleteError('Cafe not found');
      return;
    }

    // Verify organization URL matches the one we're trying to delete
    if (currentOrganization.urlName !== orgSlug) {
      setDeleteError('Organization mismatch');
      return;
    }

    // Check if confirmation text is empty
    if (!deleteConfirmText.trim()) {
      setDeleteError('Please enter the cafe name to confirm deletion');
      return;
    }

    // Check if confirmation text matches exactly (case-sensitive)
    if (deleteConfirmText.trim() !== currentOrganization.name.trim()) {
      setDeleteError('Cafe name does not match exactly. Please check spelling and case.');
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError('');

      const success = await deleteOrganization(orgSlug);

      if (success) {
        // Redirect to cafes selector or home page
        navigate('/cafes', { replace: true });
      } else {
        setDeleteError('Failed to delete cafe. Please try again.');
      }
    } catch (error) {
      console.error('Failed to delete cafe:', error);
      setDeleteError('An error occurred while deleting the cafe. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Show loading state while organization is being loaded
  if (loading || (orgSlug && (!currentOrganization || currentOrganization.urlName !== orgSlug))) {
    return (
      <div className="main-content">
        <div className="loading-container">
          <div className="loading-spinner-container">
            <div className="loading-spinner-text">Loading cafe settings...</div>
            <div className="loading-spinner-element"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if organization not found
  if (!currentOrganization) {
    return (
      <div className="main-content">
        <div className="shop-not-found">
          <h1>Cafe Not Found</h1>
          <p>The cafe you're looking for doesn't exist or you don't have access to it.</p>
          {error && <div className="error-message">{error}</div>}
          <div className="error-actions">
            <button onClick={() => navigate('/cafes')} className="btn btn-primary">
              Back to Cafes
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isOwner = isCurrentUserOwner(currentOrganization);

  return (
    <div className="main-content">
      <div className="shop-settings">
        <div className="shop-settings-header">
          <h1>Cafe Settings</h1>
          <p>Manage your cafe configuration and preferences</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {nameError && <div className="error-message">{nameError}</div>}

        <div className="settings-content">
          {/* Cafe Information */}
          <div className="settings-form">
            <h3>Cafe Information</h3>
            
            <div className="form-group">
              <label htmlFor="cafeName">Cafe Name</label>
              {editingName ? (
                <form onSubmit={(e) => { e.preventDefault(); handleUpdateName(); }}>
                  <div className="edit-name-container">
                    <input
                      type="text"
                      id="cafeName"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Enter cafe name"
                      disabled={updateLoading}
                      className="form-input"
                      required
                    />
                    <div className="form-actions">
                      <button
                        type="submit"
                        disabled={updateLoading || !newName.trim()}
                        className="btn btn-primary"
                      >
                        {updateLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        disabled={updateLoading}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="name-display">
                  <p className="form-input" style={{ backgroundColor: '#f8f9fa', cursor: 'default' }}>
                    {currentOrganization.name}
                  </p>
                  {isOwner && (
                    <button
                      onClick={handleEditName}
                      className="btn btn-ghost"
                    >
                      Edit
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Cafe URL (Permanent)</label>
              <div className="url-display">
                <p className="form-input" style={{ backgroundColor: '#f8f9fa', cursor: 'default', fontFamily: 'monospace' }}>
                  beanbuzz.app/{currentOrganization.urlName}
                </p>
                <small>This URL cannot be changed</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Created</label>
                <p className="form-input" style={{ backgroundColor: '#f8f9fa', cursor: 'default' }}>
                  {new Date(currentOrganization.createdTime).toLocaleDateString()}
                </p>
              </div>
              <div className="form-group">
                <label>Your Role</label>
                <p className="form-input" style={{ backgroundColor: '#f8f9fa', cursor: 'default' }}>
                  {isOwner ? 'Owner' : 'Member'}
                </p>
              </div>
            </div>
          </div>

          {/* Cafe Status Management */}
          {isOwner && currentOrganization && currentOrganization.urlName === orgSlug && (
            <CafeStatusManager />
          )}

          {/* Organization Settings */}
          {isOwner && (
            <div className="settings-form">
              <h3>Organization Settings</h3>
              <p>Configure your organization-specific settings as JSON</p>
              
              {settingsError && (
                <div className="error-message">{settingsError}</div>
              )}

              {settingsLoading ? (
                <div className="loading-message">Loading settings...</div>
              ) : (
                <div className="settings-editor">
                  {editingSettings ? (
                    <div className="edit-settings-container">
                      <div className="form-group">
                        <label htmlFor="settingsTextarea">Settings JSON:</label>
                        <textarea
                          id="settingsTextarea"
                          value={newSettings}
                          onChange={(e) => setNewSettings(e.target.value)}
                          placeholder='{"key": "value"}'
                          rows={10}
                          className="form-textarea"
                          disabled={settingsLoading}
                        />
                      </div>
                      <div className="form-actions">
                        <button
                          onClick={handleSaveSettings}
                          disabled={settingsLoading || !newSettings.trim()}
                          className="btn btn-primary"
                        >
                          {settingsLoading ? 'Saving...' : 'Save Settings'}
                        </button>
                        <button
                          onClick={handleCancelSettingsEdit}
                          disabled={settingsLoading}
                          className="btn btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="settings-display">
                      <div className="form-group">
                        <label>Current Settings:</label>
                        <pre className="settings-preview">
                          {(() => {
                            try {
                              return JSON.stringify(JSON.parse(settings), null, 2);
                            } catch (e) {
                              return settings || '{}';
                            }
                          })()}
                        </pre>
                        {settingsUpdatedBy && settingsUpdatedDate && (
                          <div className="settings-meta">
                            <small>
                              Last updated: {new Date(settingsUpdatedDate).toLocaleString()}
                            </small>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleEditSettings}
                        className="btn btn-ghost"
                      >
                        Edit Settings
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Owner-only sections */}
          {isOwner && (
            <div className="settings-form">
              <h3>Danger Zone</h3>
              <p>Once you delete a cafe, there is no going back. Please be certain.</p>
              
              {deleteError && (
                <div className="error-message">{deleteError}</div>
              )}

              {!showDeleteConfirm ? (
                <button 
                  onClick={handleDeleteClick}
                  className="btn btn-danger"
                  disabled={deleteLoading}
                >
                  Delete Cafe
                </button>
              ) : (
                <div className="delete-confirmation">
                  <h4>Are you absolutely sure?</h4>
                  <p>
                    This action <strong>cannot be undone</strong>. This will permanently delete the{' '}
                    <strong>{currentOrganization.name}</strong> cafe and all of its data.
                  </p>
                  
                  <div className="form-group">
                    <label htmlFor="confirmText">
                      Please type <strong>{currentOrganization.name}</strong> to confirm:
                    </label>
                    <input
                      type="text"
                      id="confirmText"
                      value={deleteConfirmText}
                      onChange={(e) => {
                        setDeleteConfirmText(e.target.value);
                        // Clear error when user starts typing
                        if (deleteError) {
                          setDeleteError('');
                        }
                      }}
                      placeholder={`Enter "${currentOrganization.name}"`}
                      disabled={deleteLoading}
                      className={`form-input ${deleteConfirmText.trim() && deleteConfirmText.trim() !== currentOrganization.name.trim() ? 'input-error' : ''}`}
                    />
                    <small className="input-hint">
                      Type the exact cafe name to confirm deletion
                    </small>
                  </div>

                  <div className="form-actions">
                    <button 
                      onClick={handleDeleteCancel}
                      className="btn btn-secondary"
                      disabled={deleteLoading}
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={handleDeleteConfirm}
                      className="btn btn-danger"
                      disabled={deleteLoading || deleteConfirmText.trim() !== currentOrganization.name.trim()}
                    >
                      {deleteLoading ? 'Deleting...' : 'I understand the consequences, delete this cafe'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Non-owner message */}
          {!isOwner && (
            <div className="settings-form">
              <div className="info-message">
                <h3>Limited Access</h3>
                <p>Only the cafe owner can modify these settings. Contact the owner to make changes to this cafe.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationSettings; 